// passwordStrength — pure heuristic-based scorer so we don't ship a
// zxcvbn-sized dependency for a simple sign-up form.
//
// Score is 0-4 (matches zxcvbn levels):
//   0  too short  (<8)
//   1  weak       (only one character class OR common pattern)
//   2  fair       (two classes)
//   3  good       (three classes + ≥ 12 chars)
//   4  strong     (four classes + ≥ 14 chars)
//
// "Class" = lowercase / UPPERCASE / digits / symbols.

export type PasswordScore = 0 | 1 | 2 | 3 | 4;

export interface PasswordStrengthResult {
  score: PasswordScore;
  label: string;
  /** Short hint shown to the user — what would push them up a level. */
  hint: string;
  /** Tailwind-friendly token: 'destructive'|'amber'|'emerald'. */
  tone: 'destructive' | 'amber' | 'emerald';
}

const COMMON = new Set<string>([
  'password', 'password1', 'password123', '12345678', '123456789',
  'qwerty123', 'welcome1', 'letmein1', 'admin1234', 'iloveyou',
  '1qaz2wsx', 'sunshine', 'monkey123',
]);

export function scorePassword(pw: string): PasswordStrengthResult {
  if (pw.length === 0) {
    return { score: 0, label: 'Tomt', hint: 'Skriv ett lösenord.', tone: 'destructive' };
  }
  if (pw.length < 8) {
    return {
      score: 0,
      label: 'För kort',
      hint: 'Minst 8 tecken krävs.',
      tone: 'destructive',
    };
  }

  const lower = /[a-z]/.test(pw);
  const upper = /[A-Z]/.test(pw);
  const digit = /\d/.test(pw);
  const symbol = /[^A-Za-z0-9]/.test(pw);
  const classes = [lower, upper, digit, symbol].filter(Boolean).length;

  if (COMMON.has(pw.toLowerCase())) {
    return {
      score: 1,
      label: 'Vanligt mönster',
      hint: 'Det här lösenordet är på en lista över de vanligaste — välj ett annat.',
      tone: 'destructive',
    };
  }

  if (classes <= 1) {
    return {
      score: 1,
      label: 'Svagt',
      hint: 'Blanda STORA/små bokstäver, siffror eller specialtecken.',
      tone: 'destructive',
    };
  }
  if (classes === 2) {
    return {
      score: 2,
      label: 'Hyfsat',
      hint: 'Lägg till en till klass (siffra eller specialtecken).',
      tone: 'amber',
    };
  }
  if (classes === 3 && pw.length < 12) {
    return {
      score: 2,
      label: 'Hyfsat',
      hint: 'Gör det längre — 12+ tecken pushar upp ett steg.',
      tone: 'amber',
    };
  }
  if (classes === 3 || (classes === 4 && pw.length < 14)) {
    return {
      score: 3,
      label: 'Bra',
      hint: pw.length < 14 ? '14+ tecken med alla 4 klasser = starkt.' : 'Bra blandning.',
      tone: 'emerald',
    };
  }
  return {
    score: 4,
    label: 'Starkt',
    hint: 'Detta lösenord är svårt att gissa.',
    tone: 'emerald',
  };
}
